import { ContentBlock } from '@tytapp/content';
import { ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { Component, HostBinding } from '@angular/core';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';

export interface HeroBlock extends ContentBlock {
    image: string;
    heading?: string;
    text?: string;
    overlayHeading?: boolean;
    alignment?: 'left' | 'center' | 'right';
    fadeBottom?: boolean;
    fullbleed?: boolean;
}

@Component({
    template: `
        <div class="image-box">
            <div class="hero-image" [responsiveBackgroundImage]="block.image">
                <div class="overlay"></div>
            </div>
            <h1 *ngIf="block.heading" [class.overlay]="block.overlayHeading">{{ block.heading }}</h1>
        </div>
        <div class="text cms-content"
            *ngIf="block.text"
            [innerHTML]="block.text | markdownToHtml | trustHtml"></div>
    `,
    styles: [`
        :host {
            background-color: #21232a;
            color: white;
            display: block;
            margin-bottom: 1em;
        }

        :host-context(.cms-page:first-child > tyt-content-view > .single-column > tyt-content-block:first-child) :host {
            margin-top: -4.1em;
        }

        .text {
            padding: 32px;
            font-size: 120%;
        }

        ::ng-deep .text p:last-child {
            margin-bottom: 0;
        }

        .hero-image {
            aspect-ratio: 16/9;
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            position: relative; // for overlay
        }

        :host.fade-bottom {
            background-color: transparent;
            .hero-image .overlay {
                position: absolute;
                inset: 0;
                background: linear-gradient(0deg, #101217 5%, transparent 90%);
            }
        }

        :host.fullbleed {
            background-color: transparent;
            .hero-image {
                margin: 0 calc(-50vw + 50%);
                width: 100vw;
                aspect-ratio: 30/9;
                .overlay {
                    inset: 0 calc(-50vw + 50%);
                    width: 100vw;
                    aspect-ratio: 30/9;
                }
            }
        }

        .image-box {
            position: relative;
        }

        h1 {
            padding: 0 32px;
            font-size: 32pt;
            font-weight: bold;

            @media (width < 600px) {
                font-size: 22pt;
            }
        }

        h1.overlay {
            position: absolute;
            bottom: 0.5em;
            width: 100%;
            text-shadow: 0px 2px 8px black;
        }

        :host[data-alignment="center"] {
            h1 {
                text-align: center;
            }

            .text {
                text-align: center;
            }
        }

        :host[data-alignment="right"] {
            h1 {
                text-align: right;
            }

            .text {
                text-align: right;
            }
        }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<HeroBlock> {
    @HostBinding('attr.data-alignment') get alignment() { return this.block.alignment; }
    @HostBinding('class.fullbleed') get fullbleed() { return this.block.fullbleed; }
    @HostBinding('class.fade-bottom') get fade() { return this.block.fadeBottom; }
}

@Component({
    template: `
        <div class="controls">
            <mat-button-toggle-group [(ngModel)]="block.alignment">
                <mat-button-toggle value="left" aria-label="Text align left">
                    <mat-icon>format_align_left</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="center" aria-label="Text align center">
                    <mat-icon>format_align_center</mat-icon>
                </mat-button-toggle>
                <mat-button-toggle value="right" aria-label="Text align right">
                    <mat-icon>format_align_right</mat-icon>
                </mat-button-toggle>
            </mat-button-toggle-group>
        </div>

        <div class="image-box">
            <tyt-image-asset
                    [(url)]="block.image"
                    [aspectRatio]="block.fullbleed ? 30/9 : 16/9"
                    [recommendedAspectRatio]="block.fullbleed ? 30/9 : 16/9"
                    [recommendedAspectRatioLabel]="block.fullbleed ? '30:9' : '16:9'"
                    [disabled]="locked"></tyt-image-asset>
            <div class="heading" [class.overlay]="block.overlayHeading">
                <mat-form-field appearance="outline" floatLabel="always">
                    <mat-label>Heading</mat-label>
                    <input type="text" name="heading" matInput [(ngModel)]="block.heading" [disabled]="locked" />
                </mat-form-field>
            </div>
        </div>

        <div style="display: flex; justify-content: flex-end;">
            <mat-checkbox matSuffix name="overlayHeading" [(ngModel)]="block.overlayHeading">
                Overlay heading
            </mat-checkbox>
        </div>

        <tyt-markdown-field
            [(value)]="block.text"
            label="Text (Markdown)"
            placeholder="Enter text..."
            [disabled]="locked"
            (keydown)="delayAutoSave()"
            ></tyt-markdown-field>

        <div>
            <mat-checkbox [(ngModel)]="block.fadeBottom">Fade bottom</mat-checkbox>
        </div>
        <div>
            <mat-checkbox [(ngModel)]="block.fullbleed">Stretch to page edge</mat-checkbox>
        </div>
    `,
    styles: [
        `
        tyt-markdown-field {
            width: 100%;
        }

        .controls {
            position: absolute;
            top: 1.5em;
            right: 1.5em;
            z-index: 10;
        }
        tyt-image-asset {
            background-color: black;
            color: white;
            border-radius: 10px;
        }

        .image-box {
            position: relative;
        }

        .heading {
            display: flex;
            gap: 1em;
            align-items: baseline;
            mat-form-field {
                flex-grow: 1;
            }

            &.overlay {
                position: absolute;
                bottom: 0;
                width: 100%;
                background: linear-gradient(180deg, transparent, #00000087 40%);
            }
        }

        :host[data-alignment="center"] ::ng-deep {
            .heading mat-form-field input[type="text"] {
                text-align: center;
            }

            tyt-markdown-field textarea {
                text-align: center;
            }
        }

        :host[data-alignment="right"] ::ng-deep {
            .heading mat-form-field input[type="text"] {
                text-align: right;
            }

            tyt-markdown-field textarea {
                text-align: right;
            }
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<HeroBlock> {
    @HostBinding('attr.data-alignment')
    get alignment() { return this.block.alignment; }
}


export const HERO_BLOCK_TYPE: ContentBlockType<HeroBlock> = {
    id: 'hero',
    label: 'Hero',
    icon: 'newspaper',
    template: {
        alignment: 'left'
    },
    preview: {
        alignment: 'left',
        heading: 'Heading',
        image: '/assets/operation-hope/support-c2a-bg.png',
        overlayHeading: true,
        text: 'Lorem ipsum dolor sit amet.'
    },
    previewSettings: {
        horizontalAlign: 'center',
        verticalAlign: 'center'
    },

    editorComponent: EditorComponent,
    viewComponent: ViewComponent
};